import { queryClient } from '$/services/client';
import { backendEndpoints } from '$/services/endpoints';
import { fetcher } from '$/services/fetcher';
import { GeneralBackendResponseObjectSchema } from '$/services/mapper/authentication';
import { validateToken } from '$/services/usecases/authentication';
import {
  GetAllInspirationsSchema,
  GetInspirationByIdSchema,
  UpdateInspirationSchema,
  type DetailedInspiration,
} from '$/services/usecases/inspirations/mapper/inspiration.schema';
import { materialsQuery } from '$/services/usecases/materials/queries';

export const getAllInspirations = async (
  signal: AbortSignal,
  country: string,
) => {
  const url = `${backendEndpoints.INSPIRATION_URL}?country=${encodeURIComponent(country)}`;
  return fetcher(url, {
    signal,
    method: 'GET',
    includeCredentials: true,
    parser: (data) => GetAllInspirationsSchema.parse(data),
  });
};

export const getInspirationById = async (
  id: string,
  signal: AbortSignal,
  ignoreMissingMaterials = false,
): Promise<DetailedInspiration | null> => {
  const inspiration = await fetcher(
    `${backendEndpoints.INSPIRATION_URL}/${id}`,
    {
      signal,
      method: 'GET',
      includeCredentials: true,
      parser: (data) => GetInspirationByIdSchema.parse(data),
    },
  );

  if (!inspiration.isSuccessful) return null;

  if (ignoreMissingMaterials) {
    return { ...inspiration.response.payload, materials: {} };
  }

  const materialIds = Object.values(inspiration.response.payload.components)
    .map((component) => component.initialMaterialId)
    .filter(Boolean);

  if (materialIds.length === 0) {
    return { ...inspiration.response.payload, materials: {} };
  }

  const { results: materials } = await queryClient.fetchQuery(
    materialsQuery({ ids: materialIds }),
  );

  if (!materials || materials.length === 0) return null;

  const materialMapping = Object.fromEntries(
    materials.filter(Boolean).map((material) => [material.id, material]),
  );

  return {
    ...inspiration.response.payload,
    materials: materialMapping,
  };
};

export const uploadInspirationThumbnail = async (
  id: string,
  image: File,
  viewId: number,
) => {
  const data = new FormData();
  data.append('file', image);
  data.append('viewId', String(viewId));

  await validateToken();

  return await fetcher(backendEndpoints.UPLOAD_INSPIRATION_THUMBNAIL_URL(id), {
    method: 'PUT',
    body: data,
    includeCredentials: true,
    parser: (data) => GeneralBackendResponseObjectSchema.parse(data),
  });
};

export const updateInspiration = async (
  id: string,
  inspiration: Pick<DetailedInspiration, 'id' | 'name' | 'category'> & {
    components: Array<{
      id: string;
      key: string;
      name: string;
      category: string;
      initialMaterialId: string | null;
      isVisible?: boolean;
    }>;
    views: Array<{
      id: number;
      components: Array<{
        id: string;
        corners: Array<[number, number]>;
        objectWidth: number;
        objectHeight: number;
      }>;
      layers?: string[];
    }>;
  },
) => {
  return fetcher(`${backendEndpoints.INSPIRATION_URL}/${id}`, {
    method: 'PATCH',
    includeCredentials: true,
    body: inspiration,
    parser: (data) => UpdateInspirationSchema.parse(data),
  });
};
